import type { SeededRandom } from '../../seeded-random';
import { countTilesWithValue, tileIndexAt, tileValueAt } from '../tile-grid';
import type { MapGenTileCoord } from '../tile-layout-types';

export interface GridStep {
  dc: number;
  dr: number;
}

export const DIRECTIONS_8: readonly GridStep[] = [
  { dc: 1, dr: 0 },
  { dc: -1, dr: 0 },
  { dc: 0, dr: 1 },
  { dc: 0, dr: -1 },
  { dc: 1, dr: 1 },
  { dc: -1, dr: 1 },
  { dc: 1, dr: -1 },
  { dc: -1, dr: -1 }
];

export function tileKey(col: number, row: number): string {
  return `${col},${row}`;
}

export function turnDirection(
  current: GridStep,
  directions: readonly GridStep[],
  rand: SeededRandom
): GridStep {
  const candidates = directions.filter((dir) => !(dir.dc === -current.dc && dir.dr === -current.dr));
  if (rand() < 0.55) {
    const diagonalBias = candidates.filter((dir) => Math.abs(dir.dc) + Math.abs(dir.dr) === 2);
    if (diagonalBias.length > 0) {
      return diagonalBias[Math.floor(rand() * diagonalBias.length)];
    }
  }
  return candidates[Math.floor(rand() * candidates.length)];
}

export function shuffleDirections(directions: readonly GridStep[], rand: SeededRandom): GridStep[] {
  const next = [...directions];
  for (let i = next.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    const temp = next[i];
    next[i] = next[j];
    next[j] = temp;
  }
  return next;
}

export function cellsFitWithForeignGap(
  grid: Uint8Array,
  width: number,
  height: number,
  cells: readonly MapGenTileCoord[],
  gapTiles: number,
  fillValue: number
): boolean {
  const own = new Set(cells.map((cell) => tileKey(cell.col, cell.row)));

  for (const cell of cells) {
    if (cell.col < 0 || cell.row < 0 || cell.col >= width || cell.row >= height) {
      return false;
    }

    if (tileValueAt(grid, width, height, cell.col, cell.row) !== 0) {
      return false;
    }

    if (gapTiles <= 0) {
      continue;
    }

    for (let row = cell.row - gapTiles; row <= cell.row + gapTiles; row++) {
      for (let col = cell.col - gapTiles; col <= cell.col + gapTiles; col++) {
        if (own.has(tileKey(col, row))) {
          continue;
        }

        if (tileValueAt(grid, width, height, col, row) === fillValue) {
          return false;
        }
      }
    }
  }

  return true;
}

export function canCommitCells(
  grid: Uint8Array,
  width: number,
  height: number,
  cells: readonly MapGenTileCoord[],
  budget: number,
  fillValue: number
): boolean {
  let newCount = 0;
  for (const cell of cells) {
    if (tileValueAt(grid, width, height, cell.col, cell.row) === 0) {
      newCount++;
    }
  }

  if (newCount === 0) {
    return false;
  }
  return countTilesWithValue(grid, fillValue) + newCount <= budget;
}

export function commitCells(
  grid: Uint8Array,
  width: number,
  height: number,
  cells: readonly MapGenTileCoord[],
  fillValue: number
): number {
  let added = 0;
  for (const cell of cells) {
    if (cell.col < 0 || cell.row < 0 || cell.col >= width || cell.row >= height) {
      continue;
    }
    const index = tileIndexAt(cell.col, cell.row, width);
    if (grid[index] === fillValue) {
      continue;
    }
    grid[index] = fillValue;
    added++;
  }
  return added;
}

export function canPlaceGuidedTile(
  grid: Uint8Array,
  width: number,
  height: number,
  col: number,
  row: number,
  ownCells: Set<string>,
  gapTiles: number,
  fillValue: number
): boolean {
  if (col < 0 || row < 0 || col >= width || row >= height) {
    return false;
  }

  if (tileValueAt(grid, width, height, col, row) !== 0) {
    return false;
  }

  if (gapTiles <= 0) {
    return true;
  }

  for (let nearbyRow = row - gapTiles; nearbyRow <= row + gapTiles; nearbyRow++) {
    for (let nearbyCol = col - gapTiles; nearbyCol <= col + gapTiles; nearbyCol++) {
      if (tileValueAt(grid, width, height, nearbyCol, nearbyRow) !== fillValue) {
        continue;
      }

      if (!ownCells.has(tileKey(nearbyCol, nearbyRow))) {
        return false;
      }
    }
  }
  return true;
}
